/**
 * Rewrite color swatch links to include the matching ?variant=ID ahead of click.
 * No click interception => no extra delay/flash from waiting fetch().
 *
 * Requires your preserve script to store selections in sessionStorage.
 */

const PRESERVE_KEY = 'nc:preserve_options:size_settype:v2'; // must match your preserve script key
const PRESERVE_NAMES = new Set(['size', 'set type']); // normalized

const FETCH_TIMEOUT_MS = 2500;

// Cache product JSON per swatch href (without query)
const productJsonCache = new Map(); // key: jsonUrl -> Promise<json|null>

function normalizeText(s) {
  return String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function readPreservedSelections() {
  try {
    const raw = sessionStorage.getItem(PRESERVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.selections) return null;

    const out = {};
    for (const [k, v] of Object.entries(parsed.selections)) {
      const nk = normalizeText(k);
      if (PRESERVE_NAMES.has(nk) && v) out[nk] = String(v).trim();
    }
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

function buildProductJsonUrl(productUrl) {
  const u = new URL(productUrl, window.location.origin);
  u.search = '';
  u.hash = '';
  u.pathname = u.pathname.replace(/\/$/, '') + '.js';
  return u.toString();
}

function withTimeout(promise, ms) {
  let t;
  const timeout = new Promise((_, rej) => {
    t = window.setTimeout(() => rej(new Error('timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(t));
}

async function fetchProductJson(jsonUrl) {
  if (productJsonCache.has(jsonUrl)) return productJsonCache.get(jsonUrl);

  const task = (async () => {
    const controller = new AbortController();
    try {
      const json = await withTimeout(
        fetch(jsonUrl, {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        }).then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        }),
        FETCH_TIMEOUT_MS
      );
      return json;
    } catch {
      try { controller.abort(); } catch {}
      return null;
    }
  })();

  productJsonCache.set(jsonUrl, task);
  return task;
}

function getOptionNameToPosition(productJson) {
  const map = new Map();

  if (Array.isArray(productJson?.options)) {
    // Common: [{name, position, values}, ...]
    for (const opt of productJson.options) {
      if (opt && typeof opt === 'object' && opt.name && opt.position) {
        map.set(normalizeText(opt.name), Number(opt.position));
      }
    }

    // Fallback: ["Size","Set Type",...]
    if (map.size === 0) {
      productJson.options.forEach((name, idx) => {
        if (typeof name === 'string') map.set(normalizeText(name), idx + 1);
      });
    }
  }

  return map;
}

function findMatchingVariantId(productJson, preserved) {
  if (!productJson || !Array.isArray(productJson.variants)) return null;

  const nameToPos = getOptionNameToPosition(productJson);
  const sizePos = nameToPos.get('size');
  const setPos = nameToPos.get('set type');

  const wantSize = preserved?.['size'] ? normalizeText(preserved['size']) : null;
  const wantSet = preserved?.['set type'] ? normalizeText(preserved['set type']) : null;

  if (!sizePos && !setPos) return null;

  const match = productJson.variants.find((v) => {
    if (!v) return false;

    const vSize = sizePos ? normalizeText(v[`option${sizePos}`]) : null;
    const vSet = setPos ? normalizeText(v[`option${setPos}`]) : null;

    if (wantSize && sizePos && vSize !== wantSize) return false;
    if (wantSet && setPos && vSet !== wantSet) return false;

    return true;
  });

  return match?.id ? String(match.id) : null;
}

function rewriteLinkHref(link, variantId) {
  try {
    const u = new URL(link.href, window.location.origin);
    if (variantId) u.searchParams.set('variant', variantId);
    link.href = u.toString();
    link.dataset.matchedVariantId = variantId || '';
  } catch {
    // ignore
  }
}

async function updateOneSwatchLink(link) {
  if (!(link instanceof HTMLAnchorElement)) return;

  const preserved = readPreservedSelections();
  if (!preserved) return;

  // Keep original URL so we can re-derive JSON endpoint even if href is already rewritten
  const baseHref = link.dataset.baseHref || link.href;
  link.dataset.baseHref = baseHref;

  const jsonUrl = buildProductJsonUrl(baseHref);
  const productJson = await fetchProductJson(jsonUrl);
  if (!productJson) return;

  const variantId = findMatchingVariantId(productJson, preserved);
  if (!variantId) return;

  rewriteLinkHref(link, variantId);
}

function schedule(fn) {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => fn(), { timeout: 800 });
  } else {
    window.setTimeout(fn, 0);
  }
}

function updateAllSwatches() {
  const links = Array.from(document.querySelectorAll('[data-color-swatch-link]'));
  if (!links.length) return;

  // Run in idle to avoid blocking rendering
  schedule(() => {
    links.forEach((link) => {
      updateOneSwatchLink(link);
    });
  });
}

// 1) initial
if (document.readyState !== 'loading') updateAllSwatches();
else document.addEventListener('DOMContentLoaded', updateAllSwatches, { once: true });

// 2) when user changes Size / Set Type (your preserve script writes to sessionStorage on change)
document.addEventListener(
  'change',
  (e) => {
    const t = e.target;
    if (!(t instanceof HTMLElement)) return;
    if (!t.closest('variant-picker')) return;
    updateAllSwatches();
  },
  { passive: true }
);

// 3) when swatches re-render (morph). Only when swatch links are among the added nodes: this used
// to rescan the whole document on every DOM change of every page.
function addsSwatchLink(mutation) {
  return Array.from(mutation.addedNodes).some(
    (node) =>
      node instanceof Element &&
      (node.matches('[data-color-swatch-link]') || node.querySelector('[data-color-swatch-link]') !== null)
  );
}

const mo = new MutationObserver((mutations) => {
  if (mutations.some(addsSwatchLink)) updateAllSwatches();
});
mo.observe(document.documentElement, { childList: true, subtree: true });

// 4) optional: warm up on hover (best effort)
document.addEventListener(
  'pointerenter',
  (e) => {
    const el = e.target instanceof Element ? e.target : null;
    if (!el) return;
    const link = el.closest('[data-color-swatch-link]');
    if (!(link instanceof HTMLAnchorElement)) return;
    updateOneSwatchLink(link);
  },
  true
);
